import styled, { css, keyframes } from 'styled-components';
import checkWhiteIcon from '../../assets/images/check-white.svg';
import chevronLeftWhiteIcon from '../../assets/images/chevron-left-white.svg';
import chevronLeftIcon from '../../assets/images/chevron-left.svg';
import chevronRightIcon from '../../assets/images/chevron-right.svg';
import closeIcon from '../../assets/images/close.svg';
import { theme } from '../../utils/theme';

const heeboText = (weight: number | string) => css`
  font-family: Heebo, sans-serif;
  font-size: 16px;
  font-weight: ${weight};
  line-height: 1.5;
  letter-spacing: 0.002em;
`;

export const hiddenAnimation = keyframes`
  0%    { opacity: 1; }
  99%   { opacity: 0; }
  100%  { opacity: 0; }
`;

export const visibleAnimation = keyframes`
  0%    { opacity: 0; }
  100%  { opacity: 1; }
`;

export const Container = styled.div<{ containerWidth?: number; containerHeight?: number }>`
  background-color: #2d2f30;
  border-radius: 14px;
  border: 1px solid #444646;
  position: absolute;
  bottom: 127px;
  right: 32px;
  overflow: hidden;
  box-sizing: border-box;
  width: ${({ containerWidth }) => (containerWidth ? `${containerWidth}px` : 'auto')};
  height: ${({ containerHeight }) => (containerHeight ? `${containerHeight}px` : 'auto')};
  transition:
    width 0.25s ease,
    height 0.25s ease;

  &.hidden {
    animation: ${hiddenAnimation} 0.3s;
  }

  &.visible {
    animation: ${visibleAnimation} 0.3s;
  }

  @media ${theme.mobileMediaQuery} {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: auto !important;
    height: auto !important;
    background: ${({ theme }) => theme.bbBlack};
    border-radius: 0;
    border: none;
    padding: 0;
    padding-top: 42px;
    z-index: 99999999;
    transition: none;

    &.hidden,
    &.visible {
      animation: none;
    }
  }
`;

export const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export const Close = styled.div<{ isBack?: boolean }>`
  display: none;
  position: absolute;
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  background: url(${({ isBack }) => (isBack ? chevronLeftWhiteIcon : closeIcon)}) center / contain no-repeat;
  cursor: pointer;
  z-index: 1;

  @media ${theme.mobileMediaQuery} {
    display: block;
    top: 24px;
    left: 24px;
    right: initial;
    width: 24px;
    height: 24px;
  }
`;

export const RootScreenContent = styled.div`
  min-width: 200px;
`;

export const SubmenuScreenContent = styled.div`
  min-width: 260px;
`;

export const ContentWrapper = styled.div<{ contentOpacity: number; isRootScreen?: boolean }>`
  opacity: ${({ contentOpacity }) => contentOpacity};
  transition: opacity 0.12s ease;
  padding: ${({ isRootScreen }) => (isRootScreen ? '10px 0' : '16px 0')};
  box-sizing: border-box;

  @media ${theme.mobileMediaQuery} {
    padding: 50px 16px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
`;

export const BackButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: url(${chevronLeftIcon}) center / contain no-repeat;
  cursor: pointer;
  flex-shrink: 0;
`;

export const HeaderTitle = styled.div`
  ${heeboText(500)}
  flex: 1;
  color: ${({ theme }) => theme.white};
  margin-left: 4px;
`;

export const HeaderAction = styled.button`
  ${heeboText(400)}
  color: ${({ theme }) => theme.disabledGray};
  text-decoration: underline;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
`;

export const OptionRow = styled.div<{ active?: boolean; isDisabled?: boolean }>`
  ${({ active }) => heeboText(active ? 500 : 400)}
  position: relative;
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: ${({ active }) => (active ? '8px 16px 8px 40px' : '8px 16px')};
  color: ${({ theme, active }) => (active ? theme.white : theme.butterGray)};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  border-radius: 4px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: rgba(255, 255, 255, 0.03);
    }
  }

  ${({ active }) =>
    active &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: 16px;
        width: 14px;
        height: 11px;
        background: url(${checkWhiteIcon}) center / contain no-repeat;
      }
    `}
`;

export const ColorOptionRow = styled(OptionRow)<{ swatchColor: string }>`
  justify-content: space-between;

  &::after {
    content: '';
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background-color: ${({ swatchColor }) => swatchColor};
    flex-shrink: 0;
  }
`;

export const NavRow = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 0 16px;
  font-weight: 500;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  gap: 4px;

  ${Header} + & {
    margin-top: 12px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: rgba(255, 255, 255, 0.03);
    }
  }
`;

export const NavRowIcon = styled.div<{ icon: string }>`
  width: 16px;
  height: 16px;
  background: url(${({ icon }) => icon}) center / contain no-repeat;
  flex-shrink: 0;
  margin-right: 4px;
`;

export const NavRowLabel = styled.div`
  ${heeboText(500)}
  flex: 1;
  margin-right: 8px;
  color: ${({ theme }) => theme.white};
`;

export const NavRowChevron = styled.div`
  width: 24px;
  height: 24px;
  background: url(${chevronRightIcon}) center / contain no-repeat;
  flex-shrink: 0;
`;

export const BoldTextRow = styled.div`
  display: flex;
  font-family: Heebo, sans-serif;
  align-items: center;
  min-height: 36px;
  padding: 0px 16px;

  @media ${theme.mobileMediaQuery} {
    padding: 0px;
  }
`;

export const BoldTextLabel = styled.div`
  ${heeboText(500)}
  flex: 1;
  color: ${({ theme }) => theme.white};
`;

export const BoldTextStatus = styled.div`
  ${heeboText(400)}
  color: ${({ theme }) => theme.disabledGray};
  margin-right: 8px;
`;

export const MobileSectionTitle = styled.div`
  ${heeboText(500)}
  color: ${({ theme }) => theme.white};
`;

export const MobileSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const MobileDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin: 24px 0;
`;

export const RadioIndicator = styled.div<{ active?: boolean }>`
  position: relative;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 2px solid ${({ theme, active }) => (active ? theme.white : 'rgba(255, 255, 255, 0.4)')};
  flex-shrink: 0;
  margin-left: 12px;

  ${({ active }) =>
    active &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: ${({ theme }) => theme.white};
        transform: translate(-50%, -50%);
      }
    `}
`;

export const MobileOptionRow = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  min-height: 24px;
  padding-top: 8px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
`;

export const MobileOptionLabel = styled.div<{ active?: boolean }>`
  ${({ active }) => heeboText(active ? 500 : 400)}
  flex: 1;
  color: ${({ theme, active }) => (active ? theme.white : theme.disabledGray)};
`;

export const MobileColorSwatch = styled.div<{ swatchColor: string }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background-color: ${({ swatchColor }) => swatchColor};
  flex-shrink: 0;
  margin-right: 12px;
`;
